import { useEffect, useState, type ReactNode } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";

interface GlobalChangesConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: ReactNode;
  impactLine: string;
  isLoading?: boolean;
  confirmLabel?: string;
  requireTypeForLargeN?: number;
  largeNCount?: number;
}

const DEFAULT_REQUIRE_TYPE_THRESHOLD = 5000;

export function GlobalChangesConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  impactLine,
  isLoading = false,
  confirmLabel = "Apply",
  requireTypeForLargeN = DEFAULT_REQUIRE_TYPE_THRESHOLD,
  largeNCount = 0,
}: GlobalChangesConfirmDialogProps) {
  const [typed, setTyped] = useState("");

  // Reset the typed input every time the dialog opens or the title changes,
  // so reopening does not silently pass the previous confirmation.
  useEffect(() => {
    if (isOpen) {
      setTyped("");
    }
  }, [isOpen, title]);

  const requiresTypedConfirm =
    largeNCount >= requireTypeForLargeN && requireTypeForLargeN > 0;
  const confirmationOk = !requiresTypedConfirm || typed === "CONFIRM";

  const handleConfirm = async () => {
    if (!confirmationOk) return;
    try {
      await onConfirm();
    } catch (err) {
      // The parent owns post-confirmation lifecycle (toast, close). Swallow
      // here so React does not warn about unhandled promise rejections.
      console.error("Global change confirmation handler failed:", err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="max-w-lg"
    >
      <div className="space-y-4">
        <div className="text-sm text-gray-700">{description}</div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          {impactLine}
        </div>

        {requiresTypedConfirm && (
          <div>
            <label
              htmlFor="global-changes-confirm"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Type <strong>CONFIRM</strong> to proceed
            </label>
            <Input
              id="global-changes-confirm"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="CONFIRM"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-gray-500">
              This operation will modify a large number of records.
              Type CONFIRM to enable the {confirmLabel} button.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLoading || !confirmationOk}
          >
            {isLoading ? "Applying..." : confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default GlobalChangesConfirmDialog;